import { Fragment } from "react";
import styled, { css, useTheme } from "styled-components";
import {
  Apple,
  Facebook,
  Google,
  OriginalDiscord,
  OriginalTelegram,
} from "common/Icons/Icons";
import { LanguageNameSpaces, OAuthPlatformType } from "@shared/types";
import Divider from "common/misc/Divider";
import useSocialLogins from "features/auth/useSocialLogins.ts";
import { MessageType } from "@shared/services/webview/types.ts";
import { generateWebViewMessageId } from "@shared/services/webview/utils.ts";
import { postMessageToNative } from "services/webview/webViewBridgeUtils.ts";
import {
  IS_WEB_VIEW,
  LARGE_MOBILE,
  TELEGRAM_AUTH_PATH,
} from "common/constants.ts";
import { getAuthSocialLoginButtonId } from "testIds";
import { useSelector } from "react-redux";
import { selectIsDarkMode } from "@shared/slices/common.slice.ts";
import { getWebsiteUrl } from "utils/misc.tsx";
import { isAndroid } from "react-device-detect";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { getNewLookTokens } from "common/NewLook/tokens";

interface SocialLoginsProps {
  isRegister?: boolean;
}

const SocialLogins = ({ isRegister }: SocialLoginsProps) => {
  const isDarkMode = useSelector(selectIsDarkMode);
  const { brandingData } = useBranding();
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);
  const showSocialLogin = brandingData?.show_social_login !== false;
  const { isNewLook, colors } = useTheme();
  const mutedText = colors.mutedText;

  const { onLoginClick } = useSocialLogins({
    openUrl: (url) => {
      if (IS_WEB_VIEW) {
        postMessageToNative({
          id: generateWebViewMessageId(),
          type: MessageType.OPEN_AUTH_SESSION,
          payload: { pathname: url },
        });
      } else {
        window.open(url, "_self");
      }
    },
    isAppSource: IS_WEB_VIEW,
  });

  if (!showSocialLogin) return null;

  const platforms = !IS_WEB_VIEW
    ? [...PLATFORMS, OAuthPlatformType.DISCORD]
    : PLATFORMS;

  const getIconStyle = (size: number, oauthPlatform?: OAuthPlatformType) => {
    const iconFilters =
      oauthPlatform === OAuthPlatformType.APPLE && isDarkMode
        ? { filter: "invert(100%)" }
        : undefined;
    return { width: size, height: size, ...iconFilters };
  };

  const getIcon = (oauthPlatform: OAuthPlatformType) => {
    const Icon = OAUTH_TYPE_TO_ICON[oauthPlatform];
    return <Icon style={getIconStyle(ICON_SIZE, oauthPlatform)} />;
  };

  const getButton = (oauthPlatform: OAuthPlatformType) => {
    const icon = getIcon(oauthPlatform);
    const onClick = () => {
      if (
        oauthPlatform === OAuthPlatformType.TELEGRAM &&
        IS_WEB_VIEW &&
        isAndroid
      ) {
        postMessageToNative({
          id: generateWebViewMessageId(),
          type: MessageType.OPEN_AUTH_SESSION,
          payload: {
            pathname: getWebsiteUrl(TELEGRAM_AUTH_PATH),
          },
        });
      } else {
        onLoginClick(oauthPlatform).catch(() => {});
      }
    };
    if (isNewLook)
      return (
        <NewLookButton
          type="button"
          data-testid={getAuthSocialLoginButtonId(oauthPlatform)}
          aria-label={OAUTH_TYPE_TO_NAME[oauthPlatform]}
          onClick={onClick}
        >
          {icon}
        </NewLookButton>
      );
    return (
      <Button
        data-testid={getAuthSocialLoginButtonId(oauthPlatform)}
        onClick={onClick}
      >
        {icon}
      </Button>
    );
  };

  return (
    <>
      <Divider
        text={
          isRegister
            ? t("social.or_register_with")
            : t(
                isNewLook
                  ? "social.or_login_with_new_look"
                  : "social.or_login_with"
              )
        }
        style={{ margin: isNewLook ? "24px 0 16px" : "20px 0 16px 0" }}
        textStyle={
          isNewLook
            ? { fontSize: 12, lineHeight: "16px", color: mutedText }
            : undefined
        }
      />
      <Wrapper $columns={platforms.length}>
        {platforms.map((platform) => (
          <Fragment key={platform}>{getButton(platform)}</Fragment>
        ))}
      </Wrapper>
    </>
  );
};

export default SocialLogins;

const ICON_SIZE = 24;

const PLATFORMS = [
  OAuthPlatformType.GOOGLE,
  OAuthPlatformType.APPLE,
  OAuthPlatformType.TELEGRAM,
];

// Accessible names for the icon-only buttons (brand names, not copy).
const OAUTH_TYPE_TO_NAME = {
  [OAuthPlatformType.GOOGLE]: "Google",
  [OAuthPlatformType.APPLE]: "Apple",
  [OAuthPlatformType.FACEBOOK]: "Facebook",
  [OAuthPlatformType.TELEGRAM]: "Telegram",
  [OAuthPlatformType.DISCORD]: "Discord",
};

const OAUTH_TYPE_TO_ICON = {
  [OAuthPlatformType.GOOGLE]: Google,
  [OAuthPlatformType.APPLE]: Apple,
  [OAuthPlatformType.FACEBOOK]: Facebook,
  [OAuthPlatformType.TELEGRAM]: OriginalTelegram,
  [OAuthPlatformType.DISCORD]: OriginalDiscord,
};

const Wrapper = styled.div<{ $columns: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 12px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      display: grid;
      grid-template-columns: repeat(${p.$columns}, minmax(0, 1fr));
      column-gap: 8px;
    `}
`;
const Button = styled.div`
  border-radius: 8px;
  cursor: pointer;
  background-color: ${(p) => p.theme.colors.tagBgColor};
  padding: 10px;
  display: flex;
  align-items: center;
`;
const NewLookButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0;
  border-radius: ${(p) => p.theme.radii.control}px;
  border: 1px solid ${(p) => p.theme.colors.inputBorderColor};
  background-color: ${(p) => p.theme.colors.cardBgColor};
  font: inherit;
  cursor: pointer;
  transition: border-color 150ms ease, background-color 150ms ease;
  svg {
    width: 20px !important;
    height: 20px !important;
  }
  @media (hover: hover) {
    &:hover {
      border-color: ${(p) => p.theme.colors.hardBorderGrey};
      background-color: ${(p) => p.theme.colors.lightBackground};
    }
  }
  &:active {
    background-color: ${(p) => p.theme.colors.lightBackground};
  }
  /* Phones: the same 44px as the fields and the main action. */
  @media (max-width: ${LARGE_MOBILE}) {
    height: 44px;
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 2px;
  }
`;
